<template>
  <!-- Fullscreen variant -->
  <div
    v-if="variant === 'fullscreen'"
    class="fixed inset-0 z-50 bg-hp-blue-800"
    :data-testid="testId"
    :data-qa="dataQa || testId"
    :data-state="variant"
    role="status"
    aria-live="polite"
    aria-busy="true"
  >
    <img
      v-if="showLogo"
      src="/penske-logo.svg"
      alt="Penske"
      class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-8 w-auto"
    />
    <div
      class="absolute inset-x-0 bottom-[10vh] sm:bottom-[12vh] pb-[calc(env(safe-area-inset-bottom)+8px)] flex flex-col items-center gap-4"
    >
      <Icon v-if="showSpinner" name="loader-circle" :class="spinnerClasses" />
      <div v-if="message || $slots.default" class="text-center">
        <p v-if="message" class="text-white/95 text-center text-xs">
          {{ message }}
        </p>
        <p v-if="subtitle" class="text-white/70 text-center text-xs mt-1">
          {{ subtitle }}
        </p>
        <slot />
      </div>
    </div>
    <!-- Version display at bottom -->
    <div
      v-if="appVersion"
      class="absolute bottom-4 left-1/2 -translate-x-1/2 pb-[calc(env(safe-area-inset-bottom)+8px)]"
    >
      <button
        class="text-white/60 hover:text-white/90 text-xs text-center transition-colors cursor-pointer underline"
        @click="goToVersion"
      >
        {{ displayVersion }}
      </button>
    </div>
  </div>

  <!-- Card variant -->
  <div
    v-else-if="variant === 'card'"
    class="fixed inset-0 z-50 flex items-center justify-center"
    :class="backdrop ? 'bg-white/20 backdrop-blur-sm' : ''"
    :data-testid="testId"
    :data-qa="dataQa || testId"
    :data-state="variant"
    role="status"
    aria-live="polite"
    aria-busy="true"
  >
    <div class="flex flex-col items-center gap-4 rounded-lg bg-white p-6 shadow-lg">
      <Icon v-if="showSpinner" name="loader-circle" :class="spinnerClasses" />
      <div v-if="message || subtitle || $slots.default" class="text-center">
        <p v-if="message" class="text-sm font-medium text-hp-gray-900">{{ message }}</p>
        <p v-if="subtitle" class="text-xs text-hp-gray-500 mt-1">{{ subtitle }}</p>
        <slot />
      </div>
    </div>
  </div>

  <!-- Overlay variant -->
  <div
    v-else-if="variant === 'overlay'"
    class="pointer-events-none fixed inset-0 z-50 flex items-center justify-center"
    :data-testid="testId"
    :data-qa="dataQa || testId"
    :data-state="variant"
    role="status"
    aria-live="polite"
    aria-busy="true"
  >
    <div class="rounded-full bg-white/80 backdrop-blur-sm shadow p-3 border border-white/60">
      <Icon v-if="showSpinner" name="loader-circle" :class="spinnerClasses" />
    </div>
  </div>

  <!-- Inline variant -->
  <div
    v-else
    class="flex flex-col items-center justify-center"
    :data-testid="testId"
    :data-qa="dataQa || testId"
    :data-state="variant"
    role="status"
    aria-live="polite"
    aria-busy="true"
  >
    <Icon v-if="showSpinner" name="loader-circle" :class="spinnerClasses" />
    <div v-if="message || subtitle || $slots.default" class="text-center mt-6">
      <p v-if="message" class="text-center text-hp-gray-700">{{ message }}</p>
      <p v-if="subtitle" class="text-center text-hp-gray-500 text-sm mt-1">
        {{ subtitle }}
      </p>
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@/components/atoms';
import { getEnvironmentConfig } from '@/services/environment';

interface Props {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  color?: string;
  strokeWidth?: number;
  message?: string;
  subtitle?: string;
  variant?: 'inline' | 'overlay' | 'fullscreen' | 'card';
  showSpinner?: boolean;
  showLogo?: boolean;
  backdrop?: boolean;
  testId?: string;
  dataQa?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  color: 'hp-blue',
  strokeWidth: 2,
  message: '',
  subtitle: '',
  variant: 'inline',
  showSpinner: true,
  showLogo: true,
  backdrop: true,
  testId: 'loader',
  dataQa: '',
});

defineOptions({ name: 'Loader' });

const sizeClasses = {
  xs: 'h-4 w-4',
  sm: 'h-8 w-8',
  md: 'h-12 w-12',
  lg: 'h-16 w-16',
  xl: 'h-24 w-24',
};

const spinnerClasses = computed(() => {
  const sizeClass = sizeClasses[props.size];
  const colorClass = props.color ? `text-${props.color}` : '';
  return `animate-spin ${sizeClass} ${colorClass}`;
});

const router = useRouter();
const appVersion = ref<string>('');

const displayVersion = computed(() => {
  if (!appVersion.value) return '';
  return appVersion.value.substring(0, 8);
});

const goToVersion = () => {
  router.push({ name: 'version' });
};

onMounted(() => {
  try {
    const config = getEnvironmentConfig();
    appVersion.value = config.appVersion;
  } catch {
    // Environment not validated yet, version will remain empty
    // This is expected during initial app load
  }
});
</script>
